import type { ReactNode } from "react";

import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { SearchButton } from "@/components/search/SearchButton";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

interface PageContainerProps {
  /** The open section — the page someone is on. */
  title: string;
  /**
   * The module the section belongs to, above the title.
   *
   * Optional because a module with one section says the same thing twice, and
   * absent on a phone it is the sidebar that is missing, not the answer to
   * "where am I" — which is why this is here at all now that the section is no
   * longer named by a tab.
   */
  breadcrumb?: string;
  /** How many rows the list holds, beside the title (#633). */
  count?: number | null;
  /** Primary actions — "New transfer", "Generate invoice". Top right. */
  actions?: ReactNode;
  /** The shared list toolbar, where the page has one. */
  toolbar?: ReactNode;
  children: ReactNode;
  /**
   * Inset the content, for a screen made of cards or a form.
   *
   * Off by default because the common case is a data table, which goes edge
   * to edge under a sticky header — padding there would leave a gutter beside
   * a scrolling table and cut the room a wide row has to work with.
   *
   * On, it matches the header's own `px-3 sm:px-6`, so the first card lines
   * up with the title above it rather than sitting a few pixels off it.
   */
  padded?: boolean;
  /**
   * Let the content own its own scrolling — a data table with a sticky header
   * needs the scroll container to be the table, not the page.
   */
  contentClassName?: string;
}

/**
 * The frame around every section: where you are, what you can do to it, then
 * the content.
 *
 * The sidebar trigger leads the header. On a phone it is the only way to the
 * navigation, so it cannot be anywhere else; on a desktop it is also the
 * collapse control, and having one button do both is what keeps the collapsed
 * state discoverable.
 *
 * The header and toolbar stay put while the content scrolls. In a screen that
 * is mostly a long table, a header that scrolls away takes the filter controls
 * with it, and the answer to "what am I looking at" ends up above the fold.
 */
export function PageContainer({
  title,
  breadcrumb,
  count,
  actions,
  toolbar,
  children,
  padded = false,
  contentClassName,
}: PageContainerProps) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header className="shrink-0 border-b px-3 sm:px-6">
        <div className="flex h-14 items-center gap-2">
          <SidebarTrigger className="-ml-1 shrink-0" />
          <Separator
            orientation="vertical"
            className="mr-1 !h-5 shrink-0"
            aria-hidden
          />

          <div className="min-w-0 flex-1">
            {breadcrumb ? (
              <p className="text-muted-foreground truncate text-xs leading-tight">
                {breadcrumb}
              </p>
            ) : null}
            <h1 className="truncate text-base leading-tight font-semibold tracking-tight sm:text-lg">
              {title}
              {typeof count === "number" ? (
                <span className="text-muted-foreground ml-2 text-sm font-normal tabular-nums">
                  {count.toLocaleString("en-IN")}
                </span>
              ) : null}
            </h1>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {actions}
            {/* Global search (#640): Ctrl+K, "/", or this on a phone. */}
            <SearchButton />
            {/* The bell (E6, #446), for the same reason: it has to be where
                the person already is, on every screen, or it is a page they
                forget to open. */}
            <NotificationBell />
            {/* Every screen, not one a module opts into (#319) — the same
                reasoning as the sidebar trigger it sits across the header
                from: feedback has to reach whatever is on screen, and a
                per-module button is one nine other tracks would forget. */}
            <FeedbackButton />
          </div>
        </div>
      </header>

      {toolbar ? (
        <div className="shrink-0 border-b px-3 py-2 sm:px-6">{toolbar}</div>
      ) : null}

      <div
        className={cn(
          "min-h-0 min-w-0 flex-1 overflow-auto",
          padded && "p-3 sm:p-6",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}
